import React from 'react';
import { Link } from 'react-router-dom';
import { useEntitlement } from '../../hooks/useApi';
import { Flex, Text, Button, Card } from '../../components/base-components';
import { FlashtoolsFeature } from '../../types';
import { v2Path } from '../nav';

/**
 * Copy of `src/components/FeatureGate.tsx`, identical in logic, differing only
 * in where `View plans` points: `v2Path('/billing')` instead of `/billing`, so
 * a gated v2 user is not thrown out of v2 mid-flow.
 *
 * Same `useEntitlement()`, same comped bypass, same `reserve_invited` purple
 * note, same `Requires {tier}.` line, same copy. The route-level gate remains
 * the single authority on access — nothing here grants anything.
 */
interface V2FeatureGateProps {
  feature: FlashtoolsFeature;
  title: string;
  description: string;
  requiredTier: string;
  children: React.ReactNode;
}

export function V2FeatureGate({
  feature,
  title,
  description,
  requiredTier,
  children,
}: V2FeatureGateProps) {
  const { data: entitlement, isLoading } = useEntitlement();

  if (isLoading) {
    return (
      <Flex justify="center" align="center" py={12}>
        <div className="w-8 h-8 border-2 border-white/30 border-t-[#3b82f6] rounded-full animate-spin" />
      </Flex>
    );
  }

  // Admin-comped users bypass every feature gate.
  if (entitlement?.comped) return <>{children}</>;

  if (entitlement && entitlement.features.includes(feature)) {
    return <>{children}</>;
  }

  const invitedPrompt =
    feature === 'buying_tools' && entitlement?.reserve_invited
      ? 'You\'ve been invited to Reserve — subscribe to unlock Buying Tools.'
      : null;

  return (
    <Flex direction="col" align="center" py={10}>
      <Card maxWidth="lg" fullWidth>
        <Flex direction="col" gap={5} align="center" py={4}>
          <div className="w-12 h-12 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-[#3b82f6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <Text variant="heading" size="xl">{title}</Text>
          <p className="text-white/50 text-sm text-center max-w-md">
            {description}
          </p>
          {invitedPrompt && (
            <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 px-4 py-2 text-sm text-purple-200">
              {invitedPrompt}
            </div>
          )}
          <p className="text-white/50 text-sm">Requires {requiredTier}.</p>
          <Link to={v2Path('/billing')}>
            <Button>View plans</Button>
          </Link>
        </Flex>
      </Card>
    </Flex>
  );
}
